import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  FaRocket,
  FaHeart,
  FaGraduationCap,
  FaCalculator,
  FaUsers,
  FaArrowRight,
  FaExternalLinkAlt,
  FaLeaf,
  FaBalanceScale,
  FaBrain,
  FaTheaterMasks,
  FaInfoCircle,
  FaCheck,
  FaTimes,
  FaMicroscope,
  FaTransgender,
  FaHandHoldingHeart,
  FaSeedling,
  FaUniversity,
  FaRainbow,
} from 'react-icons/fa';

import HeroSection from '../components/HeroSection';
import WhyChooseUs from '../components/WhyChooseUs';
import FiscalSponsorship from '../components/FiscalSponsorship';
import HowItWorks from '../components/HowItWorks';
import '../styles/Home.css';

const Home = () => {
  const [showIRSRules, setShowIRSRules] = useState(false);

  // Bridge so other components (e.g., HowItWorks) can open the IRS modal
  useEffect(() => {
    const openFromEvent = () => setShowIRSRules(true);
    window.__openIRSRules = () => setShowIRSRules(true);
    window.addEventListener('open-irs-guidelines', openFromEvent);
    return () => {
      delete window.__openIRSRules;
      window.removeEventListener('open-irs-guidelines', openFromEvent);
    };
  }, []);

  // ESC closes modal; lock body scroll while open
  useEffect(() => {
    const onEsc = (e) => e.key === 'Escape' && setShowIRSRules(false);
    if (showIRSRules) {
      window.addEventListener('keydown', onEsc);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      window.removeEventListener('keydown', onEsc);
      document.body.style.overflow = '';
    };
  }, [showIRSRules]);

  const openCalendly = () => {
    if (!window.Calendly) {
      const script = document.createElement('script');
      script.src = 'https://assets.calendly.com/assets/external/widget.js';
      script.async = true;
      document.body.appendChild(script);
      script.onload = initCalendly;
    } else {
      initCalendly();
    }
  };

  const initCalendly = () => {
    if (window.Calendly) {
      window.Calendly.initPopupWidget({
        url: 'https://calendly.com/leighton-jacobus',
        prefill: {},
        text: 'Schedule a Call',
        color: '#8B5CF6',
        branding: true,
        hideEventTypeDetails: false,
        hideGdprBanner: true,
        backgroundColor: '#111827',
        textColor: '#ffffff',
        primaryColor: '#8B5CF6',
      });
    }
  };

  return (
    <div className="home-container">
      {/* Hero + Key value props */}
      <HeroSection />
      <FiscalSponsorship />
      <HowItWorks />
      <WhyChooseUs />

   {/* Values + Causes overview (Neutral, Trust-Driven, Donor-Safe) */}
<section className="values-causes-section">
  <div className="section-content">
    <div className="values-section">
      <div className="values-header">
        <h2>Our Approach</h2>
        <p className="section-subtitle">
          We empower diverse charitable initiatives that drive measurable progress, 
          foster resilience, and create lasting value across all sectors of society.
        </p>

        <p className="section-subtitle secondary">
          Our platform is a mission-neutral infrastructure designed for integrity and impact—strictly 
          adhering to non-partisan 501(c)(3) standards to ensure every contribution serves the public good.
        </p>

        <div className="values-icons">
          <FaUsers className="value-icon" title="Community" />
          <FaMicroscope className="value-icon science" title="Science" />
          <FaGraduationCap className="value-icon" title="Education" />
          <FaLeaf className="value-icon nature" title="Environment" />
          <FaHandHoldingHeart className="value-icon heartbeat" title="Humanitarian" />
        </div>
      </div>

      <Link to="/our-story" className="learn-more-values">
        <span>Learn More About Our Values</span>
        <FaArrowRight className="arrow-icon" />
      </Link>
    </div>

    <div className="causes-overview" id="causes-overview">
      <h2>The Work We Support</h2>

      <div className="causes-header">
        <p className="section-subtitle">
          From local community building to global scientific discovery, we support a 
          full spectrum of charitable work focused on operational excellence and societal benefit.
        </p>

        <button
          type="button"
          id="irs-guidelines-trigger"
          className="irs-rules-button"
          onClick={() => setShowIRSRules(true)}
        >
          <FaInfoCircle />
          View IRS & 501(c)(3) Compliance Guidelines
        </button>
      </div>

      {/* IRS Guidelines Modal (UNCHANGED logic, but slightly updated text for "neutrality") */}
      {showIRSRules && (
        <div className="irs-modal-overlay" onClick={() => setShowIRSRules(false)}>
          <div className="irs-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowIRSRules(false)} aria-label="Close guidelines">×</button>
            <h2>Stewardship & Compliance Standards</h2>
            <div className="rules-grid">
              <div className="rules-section allowed">
                <h3><FaCheck /> Approved Charitable Missions</h3>
                <ul>
                  <li>Poverty alleviation and relief</li>
                  <li>Educational & vocational advancement</li>
                  <li>Scientific & medical research</li>
                  <li>Public health & safety</li>
                  <li>Civil rights & individual liberties</li>
                  <li>Community & infrastructure development</li>
                  <li>Conservation & resource management</li>
                  <li>Arts, culture, and heritage</li>
                </ul>
              </div>
              <div className="rules-section prohibited">
                <h3><FaTimes /> Prohibited Activities</h3>
                <ul>
                  <li>Political campaigning/Partisanship</li>
                  <li>Lobbying for legislative change</li>
                  <li>Private interest or personal gain</li>
                  <li>Unregulated commercial activity</li>
                  <li>Non-charitable distribution</li>
                  <li>Activities outside 501(c)(3) scope</li>
                </ul>
              </div>
            </div>
            <div className="rules-footer">
              <a href="https://www.irs.gov/charities-non-profits/charitable-organizations/exemption-requirements-501c3-organizations" target="_blank" rel="noopener noreferrer" className="irs-link">
                <FaExternalLinkAlt /> Official IRS Regulatory Framework
              </a>
            </div>
          </div>
        </div>
      )}

<div className="causes-categories-grid">
  {/* Category 1: Justice & Advocacy */}
  <div className="category-card">
    <div className="category-icon-wrapper">
      <FaBalanceScale className="category-icon" />
    </div>
    <h3>Civil Liberties & Justice</h3>
    <ul>
      <li>Individual Rights & Advocacy</li>
      <li>Criminal Justice Reform</li>
      <li>Economic Opportunity & Equality</li>
      <li>Immigration & Legal Services</li>
      <li>Support for Vulnerable Populations</li>
    </ul>
  </div>

  {/* Category 2: Education */}
  <div className="category-card">
    <div className="category-icon-wrapper">
      <FaGraduationCap className="category-icon" />
    </div>
    <h3>Education & Literacy</h3>
    <ul>
      <li>STEM & Technical Programs</li>
      <li>Educational Equity & Access</li>
      <li>Arts & Creative Education</li>
      <li>Adult Learning & Vocational Training</li>
      <li>Early Childhood Development</li>
    </ul>
  </div>

  {/* Category 3: Health */}
  <div className="category-card">
    <div className="category-icon-wrapper">
      <FaHandHoldingHeart className="category-icon" />
    </div>
    <h3>Health & Wellness</h3>
    <ul>
      <li>Medical & Clinical Research</li>
      <li>Mental Health & Counseling</li>
      <li>Public Health & Community Safety</li>
      <li>Healthcare Access & Navigation</li>
      <li>Emergency Relief Services</li>
    </ul>
  </div>

  {/* Category 4: Environment */}
  <div className="category-card">
    <div className="category-icon-wrapper">
      <FaLeaf className="category-icon" />
    </div>
    <h3>Stewardship & Environment</h3>
    <ul>
      <li>Conservation & Land Protection</li>
      <li>Climate Resiliency & Action</li>
      <li>Sustainable Energy & Infrastructure</li>
      <li>Environmental Health & Justice</li>
      <li>Natural Resource Management</li>
    </ul>
  </div>

  {/* Category 5: Arts & Culture */}
  <div className="category-card">
    <div className="category-icon-wrapper">
      <FaUniversity className="category-icon" />
    </div>
    <h3>Arts, Culture & Media</h3>
    <ul>
      <li>Documentary & Educational Film</li>
      <li>Performing & Visual Arts</li>
      <li>Cultural Heritage Preservation</li>
      <li>Community Arts & Engagement</li>
      <li>Public Media Initiatives</li>
    </ul>
  </div>
</div>

      <p className="section-subtitle compliance-note">
        By maintaining strict adherence to regulatory standards, we provide a secure, 
        transparent gateway for donors to support the causes they value most.
      </p>
    </div>
  </div>
</section>

      {/* Slim “Start your journey” row of primary actions */}
      <section className="start-journey-section">
        <div className="section-content">
          <h2 className="section-title">Start Your Journey</h2>
          <p className="section-subtitle">
            Whether you're just beginning or looking to scale your impact, we're here to support your vision
          </p>

          <div className="journey-grid">
            <div className="journey-card">
              <div className="icon-wrapper">
                <FaGraduationCap className="journey-icon" />
              </div>
              <h3>Impact Academy</h3>
              <p>
                Your trusted resource for fiscal sponsorship excellence. Learn, connect, and grow.
              </p>
              <Link to="/academy" className="journey-button academy">
                Explore Academy
              </Link>
            </div>

            <div className="journey-card">
              <div className="icon-wrapper">
                <FaCalculator className="journey-icon" />
              </div>
              <h3>Calculate Your Savings</h3>
              <p>
                Compare independent 501(c)(3) vs fiscal sponsorship with our cost tool.
              </p>
              <Link to="/cost-calculator" className="journey-button secondary">
                Try Calculator
              </Link>
            </div>

            <div className="journey-card">
              <div className="icon-wrapper">
                <FaRocket className="journey-icon" />
              </div>
              <h3>Launch Your Vision</h3>
              <p>
                Ready to create lasting impact? Apply today—it's free and fast.
              </p>
              <Link to="/apply" className="journey-button primary">
                Apply Now
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="final-cta-section">
        <div className="section-content">
          <h2>Ready to Make an Impact?</h2>
          <p className="cta-subtitle">
            Join our growing community of changemakers and start your journey today.
          </p>

          <div className="cta-options">
            <div className="cta-card">
              <h3>Turn Your Vision into Impact</h3>
              <p>Ready to launch your mission? Apply now and join our community of innovators.</p>
              <Link to="/apply" className="cta-button primary">
                Apply Now
                <FaArrowRight className="button-icon" />
              </Link>
            </div>

            <div className="cta-card">
              <h3>Let's Talk</h3>
              <p>Have questions? Schedule a call with our team to learn more about how we can help.</p>
              <button onClick={openCalendly} className="cta-button secondary">
                Schedule a Call
                <FaArrowRight className="button-icon" />
              </button>
            </div>
          </div>

          <div className="explore-more-footer">
            <p>Keep Exploring</p>
            <div className="explore-links">
              <Link to="/academy">Impact Academy</Link>
              <span className="separator">•</span>
              <Link to="/causes">Browse Causes</Link>
              <span className="separator">•</span>
              <Link to="/our-story">Our Story</Link>
              <span className="separator">•</span>
              <Link to="/cost-calculator">Cost Calculator</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
